import IsibuteDocument from "./IsibuteDocument";
import { newRandomHexidecimalKey } from "./utils";

class DocumentManager {
  constructor({id = null, startDocumentComponentType = "DocumentTitle"} = { id, startDocumentComponentType }) {
    this.id = id;

    const validInitialComponentTypes = ['DocumentTitle', 'DocumentParagraph'];
    if (!validInitialComponentTypes.includes(startDocumentComponentType)) {
      throw new Error(
        `Invalid initial document component type: "${startDocumentComponentType}". ` +
        `Must be one of: ${validInitialComponentTypes.join(', ')}`
      );
    }

    const initialComponentId = newRandomHexidecimalKey();
    const initialDocument = new IsibuteDocument({
      document: {
        documentComponents: {
          [initialComponentId]: {
            id: initialComponentId,
            unstyledContent: "\u200b",
            styling: [{ start: 0, end: 1, types: ["none"] }],
            ordinal: 1,
            documentComponentType: startDocumentComponentType,
            parentDocumentComponentId: null,
            firstChildDocumentComponentId: null,
            lastChildDocumentComponentId: null,
            previousDocumentComponentId: null,
            nextDocumentComponentId: null,
          }
        }
      }
    });

    this.history = [initialDocument];
    this.historyIndex = 0;
    // After undo/redo, React re-renders the contentEditable with the restored
    // document state, which causes the browser to fire a selectionchange event.
    // handleSelectionChange (in RichTextEditorHelpers) responds by calling
    // setCursorOnCurrentDocument, which deep-copies the current document and
    // pushes it as a new history entry. This effectively cancels the undo:
    //
    //   1. Cmd+Z → historyIndex decrements (undo works)
    //   2. React re-renders with the restored content
    //   3. Browser fires selectionchange
    //   4. setCursorOnCurrentDocument → clearStaleHistory (trims the entry we
    //      just undid from), then pushes a copy of the current document
    //   5. historyIndex is back where it started — undo was silently reversed
    //
    // This flag is set during undo/redo so setCursorOnCurrentDocument can skip
    // the spurious selectionchange and preserve the intended history position.
    this.suppressSelectionChange = false;
  }

  getCurrentDocument = () => {
    return this.history[this.historyIndex];
  };

  clearStaleHistory = () => {
    this.history = this.history.slice(0, this.historyIndex + 1);
  };

  addDocumentToHistoryFromData = (data) => {
    this.clearStaleHistory();
    this.history.push(new IsibuteDocument(data));
    this.historyIndex += 1;
  };

  // Syncs the browser's cursor/selection state into the document model.
  //
  // cursor{Start,End}Position: Absolute position in the entire document, counting
  // all characters from the beginning. Useful for undo/redo to restore exact cursor
  // location across the whole document.
  //
  // document{Start,End}ComponentCursorPosition: Relative position within the specific
  // component containing the cursor. Needed for text editing operations (backspace,
  // addCharacter, etc.) which operate on a component's unstyledContent string.
  //
  // Example: Title "Hello" (5 chars) followed by Paragraph "World" (5 chars),
  // with cursor between "Wor" and "ld":
  //   cursorStartPosition = 8 (absolute)
  //   documentStartComponentCursorPosition = 3 (relative to paragraph)
  setCursorOnCurrentDocument = ({
    cursorStartPosition,
    cursorEndPosition,
    documentStartComponentCursorPosition,
    documentEndComponentCursorPosition,
    documentStartComponentNode,
    documentEndComponentNode,
  }) => {
    if (this.suppressSelectionChange) {
      this.suppressSelectionChange = false;
      return;
    }

    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();

    nextDocument.cursorStartPosition = cursorStartPosition;
    nextDocument.cursorEndPosition = cursorEndPosition;
    nextDocument.currentStartNodeCursorPosition = documentStartComponentCursorPosition;
    nextDocument.currentEndNodeCursorPosition = documentEndComponentCursorPosition;
    nextDocument.pendingStyleTypes = null;

    const documentStartComponentNodeKeyPath = documentStartComponentNode.id.split("-");
    nextDocument.currentStartNode = nextDocument.getDocumentComponentByKeyPath({
      keyPath: documentStartComponentNodeKeyPath
    });

    const documentEndComponentNodeKeyPath = documentEndComponentNode.id.split("-");
    nextDocument.currentEndNode = nextDocument.getDocumentComponentByKeyPath({
      keyPath: documentEndComponentNodeKeyPath
    });

    this.history.push(nextDocument);
    this.historyIndex += 1;
  }

  processEvent = ({
    key,
    altKeyIsPressed,
    ctrlKeyIsPressed,
    metaKeyIsPressed,
    shiftKeyIsPressed,
  }) => {
    // All Are Pressed

    if (altKeyIsPressed && ctrlKeyIsPressed && metaKeyIsPressed && shiftKeyIsPressed) {
      // TODO
    }

    // Three Are Pressed

    if (!altKeyIsPressed && ctrlKeyIsPressed && metaKeyIsPressed && shiftKeyIsPressed) {
      // TODO
    }

    if (altKeyIsPressed && !ctrlKeyIsPressed && metaKeyIsPressed && shiftKeyIsPressed) {
      // TODO
    }

    if (altKeyIsPressed && ctrlKeyIsPressed && !metaKeyIsPressed && shiftKeyIsPressed) {
      // TODO
    }

    if (altKeyIsPressed && ctrlKeyIsPressed && metaKeyIsPressed && !shiftKeyIsPressed) {
      // TODO
    }

    // Two are pressed

    if (!altKeyIsPressed && !ctrlKeyIsPressed && metaKeyIsPressed && shiftKeyIsPressed) {
      this.processEventWhenMetaAndShiftKeysArePressed({ key });
    }

    if (!altKeyIsPressed && ctrlKeyIsPressed && !metaKeyIsPressed && shiftKeyIsPressed) {
      // TODO
    }

    if (!altKeyIsPressed && ctrlKeyIsPressed && metaKeyIsPressed && !shiftKeyIsPressed) {
      // TODO
    }

    if (altKeyIsPressed && !ctrlKeyIsPressed && !metaKeyIsPressed && shiftKeyIsPressed) {
      // TODO
    }

    if (altKeyIsPressed && !ctrlKeyIsPressed && metaKeyIsPressed && !shiftKeyIsPressed) {
      // TODO
    }

    if (altKeyIsPressed && ctrlKeyIsPressed && !metaKeyIsPressed && !shiftKeyIsPressed) {
      // TODO
    }

    // One is pressed

    if (!altKeyIsPressed && !ctrlKeyIsPressed && !metaKeyIsPressed && shiftKeyIsPressed) {
      this.processEventWhenShiftKeyIsPressed({ key });
    }

    if (!altKeyIsPressed && !ctrlKeyIsPressed && metaKeyIsPressed && !shiftKeyIsPressed) {
      this.processEventWhenMetaKeyIsPressed({ key });
    }

    if (!altKeyIsPressed && ctrlKeyIsPressed && !metaKeyIsPressed && !shiftKeyIsPressed) {
      // TODO
    }

    if (altKeyIsPressed && !ctrlKeyIsPressed && !metaKeyIsPressed && !shiftKeyIsPressed) {
      // TODO
    }

    // None are pressed

    if (!altKeyIsPressed && !ctrlKeyIsPressed && !metaKeyIsPressed && !shiftKeyIsPressed) {
      this.processEventWhenNoSpecialKeysArePressed({ key });
    }
  };

  processEventWhenMetaAndShiftKeysArePressed = ({ key }) => {
    switch (key) {
      case 'Alt':
        break;
      case 'ArrowDown':
        break;
      case 'ArrowLeft':
        break;
      case 'ArrowRight':
        break;
      case 'ArrowUp':
        break;
      case 'Backspace':
        break;
      case 'Control':
        break;
      case 'Enter':
        break;
      case 'Meta':
        break;
      case 'Shift':
        break;
      default:
        if (key == 'z') {
          if (this.historyIndex < this.history.length - 1) {
            this.historyIndex += 1;
            this.suppressSelectionChange = true;
          }
        }
    }
  };

  processEventWhenMetaKeyIsPressed = ({ key }) => {
    switch (key) {
      case 'Alt':
        break;
      case 'ArrowDown':
        break;
      case 'ArrowLeft':
        break;
      case 'ArrowRight':
        break;
      case 'ArrowUp':
        break;
      case 'Backspace':
        break;
      case 'Control':
        break;
      case 'Enter':
        break;
      case 'Meta':
        break;
      case 'Shift':
        break;
      default:
        if (key == 'z') {
          if (this.historyIndex > 0) {
            this.historyIndex -= 1;
            this.suppressSelectionChange = true;
          }
        }
    }
  };

  processEventWhenShiftKeyIsPressed = ({ key }) =>{
    this.processEventWhenNoSpecialKeysArePressed({ key });
  };

  processEventWhenNoSpecialKeysArePressed = ({ key }) => {
    let nextDocument;

    switch (key) {
      case 'Alt':
        break;
      case 'ArrowDown':
        break;
      case 'ArrowLeft':
        this.clearStaleHistory();

        nextDocument = this.getCurrentDocument().deepCopy();
        nextDocument.pendingStyleTypes = null;
        nextDocument.arrowLeft();
        this.history.push(nextDocument);
        this.historyIndex += 1;
  
        break;
      case 'ArrowRight':
        this.clearStaleHistory();

        nextDocument = this.getCurrentDocument().deepCopy();
        nextDocument.pendingStyleTypes = null;
        nextDocument.arrowRight();
        this.history.push(nextDocument);
        this.historyIndex += 1;
  
        break;
      case 'ArrowUp':
        break;
      case 'Backspace':
        this.clearStaleHistory();

        nextDocument = this.getCurrentDocument().deepCopy();
        nextDocument.backspace();
        this.history.push(nextDocument);
        this.historyIndex += 1;
  
        break;
      case 'Control':
        break;
      case 'Enter':
        this.clearStaleHistory();

        nextDocument = this.getCurrentDocument().deepCopy();
        nextDocument.enter();
        this.history.push(nextDocument);
        this.historyIndex += 1;
  
        break;
      case 'Meta':
        break;
      case 'Shift':
        break;
      default:
        this.clearStaleHistory();

        nextDocument = this.getCurrentDocument().deepCopy();
        nextDocument.addCharacter(key);

        if (nextDocument.pendingStyleTypes !== null) {
          const charPosition = nextDocument.currentEndNodeCursorPosition - 1;
          const currentTypes = nextDocument.currentEndNode.getStyleTypesAtPosition(charPosition);
          const realCurrentTypes = currentTypes.filter(t => t !== 'none');

          const typesToRemove = realCurrentTypes.filter(t => !nextDocument.pendingStyleTypes.includes(t));
          if (typesToRemove.length > 0) {
            nextDocument.currentEndNode.removeStyle({
              start: charPosition,
              end: charPosition,
              types: typesToRemove,
            });
          }

          const typesToAdd = nextDocument.pendingStyleTypes.filter(t => t !== 'none' && !realCurrentTypes.includes(t));
          for (const type of typesToAdd) {
            nextDocument.currentEndNode.applyStyle({
              start: charPosition,
              end: charPosition,
              types: [type],
            });
          }
        }

        this.history.push(nextDocument);
        this.historyIndex += 1;
    }
  };

  getActiveStyleTypes = () => {
    const doc = this.getCurrentDocument();
    if (doc.pendingStyleTypes !== null) {
      return [...doc.pendingStyleTypes];
    }
    return doc.getActiveStyleTypes();
  };

  getActiveFormat = () => {
    return this.getCurrentDocument().getActiveFormat();
  };

  applyFormatToSelectedRange = (format) => {
    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();
    nextDocument.applyFormat(format);
    this.history.push(nextDocument);
    this.historyIndex += 1;
  };

  applyStyleToSelectedRange = (style) => {
    const currentDoc = this.getCurrentDocument();

    if (currentDoc.cursorEndPosition <= 1 || currentDoc.cursorEndPosition <= currentDoc.cursorStartPosition) {
      const effectiveStyles = this.getActiveStyleTypes();
      let nextPending;
      if (effectiveStyles.includes(style.type)) {
        const remaining = effectiveStyles.filter(t => t !== style.type && t !== 'none');
        nextPending = remaining.length > 0 ? remaining : ['none'];
      } else {
        const base = effectiveStyles.filter(t => t !== 'none');
        nextPending = [...base, style.type];
      }

      this.clearStaleHistory();
      const nextDocument = currentDoc.deepCopy();
      nextDocument.pendingStyleTypes = nextPending;
      this.history.push(nextDocument);
      this.historyIndex += 1;
      return;
    }

    this.clearStaleHistory();

    const nextDocument = currentDoc.deepCopy();
    nextDocument.pendingStyleTypes = null;
    nextDocument.applyStyle(style);
    this.history.push(nextDocument);
    this.historyIndex += 1;
  };

  getActiveHref = () => {
    return this.getCurrentDocument().getActiveHref();
  };

  applyLinkToSelectedRange = (href) => {
    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();
    nextDocument.pendingStyleTypes = null;
    nextDocument.removeStyleFromRange({ type: 'a' });
    nextDocument.applyStyleToRange({ type: 'a', href });
    this.history.push(nextDocument);
    this.historyIndex += 1;
  };

  removeLinkFromSelectedRange = () => {
    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();
    nextDocument.pendingStyleTypes = null;
    nextDocument.removeStyleFromRange({ type: 'a' });
    this.history.push(nextDocument);
    this.historyIndex += 1;
  };

  pasteText = (text) => {
    if (!text) return;

    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();

    const normalizedText = text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n+/g, '\n');

    for (const char of normalizedText) {
      if (char === '\n') {
        nextDocument.enter();
      } else {
        nextDocument.addCharacter(char);
      }
    }

    this.history.push(nextDocument);
    this.historyIndex += 1;
  };

  insertImage = ({ caption, altText, pendingImageData, pendingImageFileName, displaySizeVariant }) => {
    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();
    nextDocument.insertImageComponent({
      caption: caption || '',
      altText: altText || pendingImageFileName || 'Image',
      pendingImageData,
      pendingImageFileName,
      displaySizeVariant: displaySizeVariant || 'large',
    });
    this.history.push(nextDocument);
    this.historyIndex += 1;
  };

  deleteImageComponent = (componentId) => {
    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();
    nextDocument.deleteImageComponent(componentId);
    this.history.push(nextDocument);
    this.historyIndex += 1;
  };

  updateImageCaption = ({ componentId, caption }) => {
    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();
    nextDocument.updateImageCaption({ componentId, caption });
    this.history.push(nextDocument);
    this.historyIndex += 1;
  };

  insertParagraphAfterImage = ({ componentId }) => {
    this.clearStaleHistory();

    const nextDocument = this.getCurrentDocument().deepCopy();
    nextDocument.insertParagraphAfterImage({ componentId });
    this.history.push(nextDocument);
    this.historyIndex += 1;
  };
};

export default DocumentManager;
